// src/pages/HomePage.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, Download, Search, Users, FileText, ArrowRight,
  GraduationCap, Sparkles, TrendingUp, Clock, Star, Zap,
  ChevronRight, Award, Target, CheckCircle, Laptop, Cog,
  TrendingUp as TrendingUpIcon, FlaskConical, Heart, Sprout,
  School, Eye, Filter, BarChart3, Crown, Building,
  Grid3x3, List, RefreshCw, Tag, Calendar,
  DownloadCloud, Upload, Bookmark, MessageSquare, Layers,
  AlertCircle, Loader, ExternalLink, Share2, X
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import SearchBar from '../components/common/SearchBar';
import Button from '../components/common/Button';
import MaterialCard from '../components/common/MaterialCard';

// Import API service
import { publicAPI } from '../services/api';

// Enhanced Interactive Stats Component
const InteractiveStats = ({ stats, onStatClick }) => {
  const [hoveredStat, setHoveredStat] = useState(null);

  const statVariants = {
    hidden: { scale: 0, opacity: 0, rotateX: 90 },
    visible: {
      scale: 1,
      opacity: 1,
      rotateX: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  // Ensure stats is an array and has items
  if (!stats || stats.length === 0) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
            <div className="h-8 w-20 bg-white/20 rounded mb-2 animate-pulse"></div>
            <div className="h-4 w-24 bg-white/20 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1
          }
        }
      }}
    >
      {stats.map((stat, index) => (
        <motion.div
          key={stat.id || index}
          variants={statVariants}
          whileHover={{
            scale: 1.05,
            y: -5,
            transition: { duration: 0.2 }
          }}
          whileTap={{ scale: 0.95 }}
          onMouseEnter={() => setHoveredStat(index)}
          onMouseLeave={() => setHoveredStat(null)}
          onClick={() => onStatClick && onStatClick(stat)}
          className="relative cursor-pointer"
        >
          <div className={`
            bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-lg rounded-2xl p-4 
            border border-white/20 shadow-lg shadow-blue-500/10
            transition-all duration-300 overflow-hidden
            ${hoveredStat === index ? 'ring-2 ring-white/30' : ''}
          `}>
            {/* Animated background effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-400/10 to-blue-500/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 rounded-lg ${hoveredStat === index ? 'bg-white/30' : 'bg-white/20'
                  } backdrop-blur-sm`}>
                  {stat.icon === 'download' && <Download className="w-4 h-4 text-blue-200" />}
                  {stat.icon === 'users' && <Users className="w-4 h-4 text-green-200" />}
                  {stat.icon === 'book' && <BookOpen className="w-4 h-4 text-purple-200" />}
                  {stat.icon === 'trending' && <TrendingUp className="w-4 h-4 text-yellow-200" />}
                </div>
                {stat.trending && (
                  <motion.div
                    animate={{
                      scale: hoveredStat === index ? [1, 1.2, 1] : 1
                    }}
                    transition={{ duration: 0.5 }}
                    className={`text-xs px-2 py-1 rounded-full ${stat.trending === 'up'
                      ? 'bg-green-500/30 text-green-200 border border-green-500/50'
                      : 'bg-red-500/30 text-red-200 border border-red-500/50'
                      } backdrop-blur-sm`}
                  >
                    {stat.trending === 'up' ? '↗' : '↘'} {stat.change}%
                  </motion.div>
                )}
              </div>

              <motion.div
                className="text-2xl md:text-3xl font-bold text-white mb-1"
                animate={{
                  scale: hoveredStat === index ? [1, 1.05, 1] : 1
                }}
                transition={{ duration: 0.3 }}
              >
                {stat.value}{stat.suffix || ''}
              </motion.div>

              <div className="text-sm text-blue-200/90 font-medium">
                {stat.label}
              </div>
            </div>
          </div>

          {/* Tooltip */}
          <AnimatePresence>
            {hoveredStat === index && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                className="absolute bottom-full mb-3 left-1/2 transform -translate-x-1/2 w-48"
              >
                <div className="bg-gray-900/95 backdrop-blur-lg rounded-lg p-3 text-xs text-gray-200 shadow-2xl border border-gray-700/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-3 h-3 text-blue-400" />
                    <span className="font-semibold">Click to explore</span>
                  </div>
                  <p className="text-gray-300">
                    {stat.description || `View all ${stat.label.toLowerCase()}`}
                  </p>
                </div>
                <div className="w-3 h-3 bg-gray-900/95 transform rotate-45 mx-auto -mt-1.5 border-r border-b border-gray-700/50" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </motion.div>
  );
};

// Enhanced Material Carousel with better search integration
const MaterialCarousel = ({ materials, title, subtitle, onMaterialClick, type = 'recent', searchTerm = '', loading }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [filteredMaterials, setFilteredMaterials] = useState(materials);

  useEffect(() => {
    if (searchTerm) {
      const filtered = materials.filter(material =>
        material.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.course?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredMaterials(filtered);
    } else {
      setFilteredMaterials(materials);
    }
  }, [searchTerm, materials]);

  if (loading) {
    return (
      <div className="py-8 animate-pulse">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <div className="h-6 w-32 bg-blue-100 rounded-full mb-2"></div>
            <div className="h-8 w-48 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-md h-[320px] flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-gray-100 rounded-lg mb-4"></div>
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-6"></div>
              </div>
              <div className="space-y-3">
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
              <div className="pt-4 border-t border-gray-100 mt-6 flex justify-between items-center">
                <div className="h-4 bg-gray-200 rounded w-12"></div>
                <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!filteredMaterials.length) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-12 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200"
      >
        <div className="inline-flex p-4 bg-blue-50 rounded-full mb-4">
          <FileText className="w-12 h-12 text-blue-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Materials Found</h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          {searchTerm
            ? `No ${type === 'recent' ? 'recent' : 'popular'} materials found matching "${searchTerm}"`
            : `No ${type === 'recent' ? 'recently uploaded' : 'popular'} materials available`}
        </p>
        {searchTerm && (
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            Clear Search
          </button>
        )}
      </motion.div>
    );
  }

  const visibleMaterials = viewMode === 'grid'
    ? filteredMaterials.slice(currentIndex * 4, currentIndex * 4 + 4)
    : [filteredMaterials[currentIndex]];

  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4"
      >
        <div>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-blue-50 text-blue-700 px-4 py-2 rounded-full mb-2 border border-blue-200">
            <Zap className="w-4 h-4" />
            <span className="font-medium">{title}</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
            {subtitle}
          </h3>
          {searchTerm && (
            <p className="text-gray-600 mt-2">
              Showing results for: <span className="font-semibold text-blue-600">"{searchTerm}"</span>
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-all ${viewMode === 'grid'
                ? 'bg-white shadow-sm text-blue-600'
                : 'text-gray-600 hover:bg-gray-200'
                }`}
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${viewMode === 'list'
                ? 'bg-white shadow-sm text-blue-600'
                : 'text-gray-600 hover:bg-gray-200'
                }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>

          <div className="flex gap-1">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
              disabled={currentIndex === 0}
              className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5 transform rotate-180" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setCurrentIndex(prev =>
                Math.min(Math.ceil(filteredMaterials.length / 4) - 1, prev + 1)
              )}
              disabled={currentIndex >= Math.ceil(filteredMaterials.length / 4) - 1}
              className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`${currentIndex}-${viewMode}`}
          initial={{ opacity: 0, x: viewMode === 'list' ? -20 : 0 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: viewMode === 'list' ? 20 : 0 }}
          transition={{ duration: 0.3 }}
          className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6' : 'max-w-4xl mx-auto'}
        >
          {visibleMaterials.map((material, index) => (
            <motion.div
              key={material._id || material.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              whileHover={{
                y: -5,
                transition: { duration: 0.2 }
              }}
            >
              <MaterialCard
                material={material}
                onClick={() => onMaterialClick(material)}
                type={type}
                compact={viewMode === 'list'}
              />
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>

      {viewMode === 'list' && filteredMaterials.length > 1 && (
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-4 bg-gray-100 rounded-full px-6 py-2">
            <span className="text-sm text-gray-600 font-medium">
              {currentIndex + 1} of {filteredMaterials.length}
            </span>
            <div className="flex gap-2">
              {Array.from({ length: Math.min(5, filteredMaterials.length) }, (_, idx) => {
                let pageIdx;
                if (filteredMaterials.length <= 5) {
                  pageIdx = idx;
                } else if (currentIndex <= 2) {
                  pageIdx = idx;
                } else if (currentIndex >= filteredMaterials.length - 3) {
                  pageIdx = filteredMaterials.length - 5 + idx;
                } else {
                  pageIdx = currentIndex - 2 + idx;
                }

                return (
                  <button
                    key={pageIdx}
                    onClick={() => setCurrentIndex(pageIdx)}
                    className={`w-2 h-2 rounded-full transition-all ${pageIdx === currentIndex
                      ? 'bg-blue-600 w-6'
                      : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced School Explorer with search
const SchoolExplorer = ({ schools, onSchoolClick, searchQuery = '', loading }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredSchools, setFilteredSchools] = useState(schools);

  useEffect(() => {
    let filtered = schools;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(school =>
        school.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        school.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(school => {
        const name = school.name.toLowerCase();
        switch (selectedCategory) {
          case 'popular':
            return school.popularity > 80;
          case 'science':
            return name.includes('science') || name.includes('computing') || name.includes('engineering');
          case 'arts':
            return name.includes('arts') || name.includes('social') || name.includes('education');
          case 'business':
            return name.includes('business') || name.includes('economics') || name.includes('management');
          default:
            return true;
        }
      });
    }

    setFilteredSchools(filtered);
  }, [searchQuery, selectedCategory, schools]);

  const categories = [
    { id: 'all', label: 'All Schools', icon: Building },
    { id: 'popular', label: 'Most Popular', icon: Crown },
    { id: 'science', label: 'Science & Tech', icon: FlaskConical },
    { id: 'arts', label: 'Arts & Humanities', icon: GraduationCap },
    { id: 'business', label: 'Business', icon: TrendingUp }
  ];

  const getSchoolIcon = (schoolName) => {
    const name = schoolName.toLowerCase();
    if (name.includes('computing') || name.includes('informatics')) return Laptop;
    if (name.includes('engineering')) return Cog;
    if (name.includes('business') || name.includes('economics')) return TrendingUpIcon;
    if (name.includes('science') || name.includes('natural')) return FlaskConical;
    if (name.includes('social')) return Users;
    if (name.includes('health') || name.includes('medicine')) return Heart;
    if (name.includes('education')) return GraduationCap;
    if (name.includes('agriculture')) return Sprout;
    return School;
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="h-6 w-32 bg-purple-100 rounded-full mb-2"></div>
            <div className="h-8 w-48 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-md h-[340px] flex flex-col justify-between">
              <div className="bg-gradient-to-r from-purple-200 to-pink-200 h-32 p-6 flex flex-col justify-between">
                <div className="w-12 h-12 bg-white/30 rounded-xl"></div>
                <div>
                  <div className="h-5 bg-white/40 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-white/30 rounded w-1/3"></div>
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
                <div className="grid grid-cols-3 gap-3 pt-6 border-t border-gray-100">
                  <div className="text-center">
                    <div className="h-6 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
                    <div className="h-3 bg-gray-100 rounded w-12 mx-auto"></div>
                  </div>
                  <div className="text-center">
                    <div className="h-6 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
                    <div className="h-3 bg-gray-100 rounded w-12 mx-auto"></div>
                  </div>
                  <div className="text-center">
                    <div className="h-6 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
                    <div className="h-3 bg-gray-100 rounded w-12 mx-auto"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // If no schools, show empty state
  if (!schools || schools.length === 0) {
    return (
      <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200">
        <div className="inline-flex p-4 bg-purple-50 rounded-full mb-4">
          <Building className="w-12 h-12 text-purple-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Schools Available</h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Schools will appear here once they are added to the system.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-50 text-purple-700 px-4 py-2 rounded-full mb-2 border border-purple-200">
            <Award className="w-4 h-4" />
            <span className="font-medium">School Explorer</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
            Browse <span className="text-purple-600">Schools</span>
          </h3>
          {searchQuery && (
            <p className="text-gray-600 mt-2">
              Searching for: <span className="font-semibold">"{searchQuery}"</span>
            </p>
          )}
        </div>
      </motion.div>

      {/* Category Filters */}
      <motion.div
        className="flex flex-wrap gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        {categories.map((category) => {
          const Icon = category.icon;
          return (
            <motion.button
              key={category.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-3 rounded-xl flex items-center gap-3 transition-all ${selectedCategory === category.id
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm border border-gray-200'
                }`}
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium">{category.label}</span>
            </motion.button>
          );
        })}
      </motion.div>

      {/* Schools Grid */}
      {filteredSchools.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSchools.map((school, index) => {
            const Icon = getSchoolIcon(school.name);
            return (
              <motion.div
                key={school._id || school.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                whileHover={{
                  y: -8,
                  transition: { duration: 0.2 }
                }}
              >
                <div
                  className="group bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 overflow-hidden"
                  onClick={() => onSchoolClick(school)}
                >
                  {/* Header with gradient */}
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        {school.popularity > 80 && (
                          <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                            <Crown className="w-4 h-4 text-yellow-300" />
                            <span className="text-sm font-medium text-white">Top Rated</span>
                          </div>
                        )}
                      </div>
                      <h4 className="text-xl font-bold text-white mb-2">{school.name}</h4>
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-white/80" />
                        <span className="text-sm text-white/80">
                          {school.departments?.length || 0} Departments
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {school.description || `Explore ${school.departments?.length || 0} departments with specialized programs.`}
                    </p>

                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {school.materials}
                        </div>
                        <div className="text-xs text-gray-500">Materials</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {school.teachers}
                        </div>
                        <div className="text-xs text-gray-500">Teachers</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {school.courses}
                        </div>
                        <div className="text-xs text-gray-500">Courses</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${school.popularity > 80
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-700'
                        }`}>
                        {school.popularity || 100}% popular
                      </div>
                      <div className="flex items-center gap-1 text-purple-600 group-hover:text-purple-700">
                        <span className="text-sm font-medium">Explore</span>
                        <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200"
        >
          <div className="inline-flex p-4 bg-purple-50 rounded-full mb-4">
            <Search className="w-12 h-12 text-purple-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Schools Found</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {searchQuery
              ? `No schools found matching "${searchQuery}"`
              : 'No schools data available'}
          </p>
          {searchQuery && (
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
            >
              Clear Search
            </button>
          )}
        </motion.div>
      )}
    </div>
  );
};

// Quick Search Results Component
const QuickSearchResults = ({ query, results, onClose, onMaterialClick }) => {
  if (!query || results.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50"
    >
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            Found {results.length} results for "{query}"
          </span>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {results.slice(0, 5).map((result) => (
          <div
            key={result._id}
            className="p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
            onClick={() => {
              onMaterialClick(result);
              onClose();
            }}
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{result.title}</h4>
                <p className="text-sm text-gray-500 mt-1">
                  {result.course?.title || 'Course'} • {result.teacher?.fullName || 'Teacher'}
                </p>
              </div>
            </div>
          </div>
        ))}

        {results.length > 5 && (
          <div className="p-3 text-center border-t border-gray-100">
            <button
              onClick={() => {
                window.location.href = `/search?q=${encodeURIComponent(query)}`;
                onClose();
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View all {results.length} results
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  // State for real data from backend
  const [schools, setSchools] = useState([]);
  const [recentMaterials, setRecentMaterials] = useState([]);
  const [popularMaterials, setPopularMaterials] = useState([]);
  const [platformStats, setPlatformStats] = useState([]);
  const [platformFeatures, setPlatformFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [quickSearchResults, setQuickSearchResults] = useState([]);
  const [showQuickResults, setShowQuickResults] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [isSearching, setIsSearching] = useState(!!initialSearch);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Perform quick search when query changes
  useEffect(() => {
    if (searchQuery.trim().length > 2) {
      const delayDebounce = setTimeout(() => {
        performQuickSearch(searchQuery);
      }, 500);

      return () => clearTimeout(delayDebounce);
    } else {
      setQuickSearchResults([]);
      setShowQuickResults(false);
    }
  }, [searchQuery]);

  const performQuickSearch = async (query) => {
    try {
      const response = await publicAPI.getMaterials({
        search: query,
        limit: 10
      });

      const results = response.data?.materials || response.data || [];
      setQuickSearchResults(results);
      setShowQuickResults(true);
    } catch (err) {
      console.error('Quick search failed:', err);
      setQuickSearchResults([]);
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching home page data...');

      const [
        schoolsResponse,
        recentResponse,
        popularResponse,
        statsResponse
      ] = await Promise.all([
        publicAPI.getSchools({ limit: 12 }).catch(err => {
          console.warn('Schools fetch failed:', err);
          return { data: [] };
        }),
        publicAPI.getMaterials({
          limit: 12,
          sort: '-createdAt',
          page: 1
        }).catch(err => {
          console.warn('Recent materials fetch failed:', err);
          return { data: [] };
        }),
        publicAPI.getMaterials({
          limit: 12,
          sort: '-downloads',
          page: 1
        }).catch(err => {
          console.warn('Popular materials fetch failed:', err);
          return { data: [] };
        }),
        publicAPI.getPlatformStats().catch(err => {
          console.warn('Stats fetch failed:', err);
          return { data: { stats: [] } };
        })
      ]);

      console.log('Schools response:', schoolsResponse);
      console.log('Recent response:', recentResponse);
      console.log('Popular response:', popularResponse);

      // Handle different response structures
      const schoolsData = schoolsResponse.data?.schools || schoolsResponse.data || schoolsResponse || [];
      setSchools(schoolsData);

      // Handle materials
      let recentMaterialsData = [];
      let popularMaterialsData = [];

      if (recentResponse.data) {
        if (Array.isArray(recentResponse.data)) {
          recentMaterialsData = recentResponse.data;
        } else if (recentResponse.data.materials) {
          recentMaterialsData = recentResponse.data.materials;
        } else if (recentResponse.data.results) {
          recentMaterialsData = recentResponse.data.results;
        }
      }

      if (popularResponse.data) {
        if (Array.isArray(popularResponse.data)) {
          popularMaterialsData = popularResponse.data;
        } else if (popularResponse.data.materials) {
          popularMaterialsData = popularResponse.data.materials;
        } else if (popularResponse.data.results) {
          popularMaterialsData = popularResponse.data.results;
        }
      }

      setRecentMaterials(recentMaterialsData);
      setPopularMaterials(popularMaterialsData);

      // Process platform stats
      const statsData = statsResponse.data || statsResponse || {};

      if (statsData.stats && statsData.stats.length > 0) {
        setPlatformStats(statsData.stats);
      } else {
        // Fallback to basic stats if not available
        setPlatformStats([
          { id: 1, label: 'Materials', value: statsData.totalMaterials || 0, icon: 'book' },
          { id: 2, label: 'Downloads', value: statsData.totalDownloads || 0, icon: 'download' },
          { id: 3, label: 'Teachers', value: statsData.activeTeachers || 0, icon: 'users' },
          { id: 4, label: 'Schools', value: statsData.activeSchools || 0, icon: 'building' }
        ]);
      }

      // Set features from your data file or API
      setPlatformFeatures([
        {
          id: 1,
          title: "Organized Repository",
          description: "All materials categorized by school, department, year, and course for easy navigation.",
          icon: "BookOpen",
          bgColor: "bg-green-100",
          iconColor: "text-green-600"
        },
        {
          id: 2,
          title: "Advanced Search",
          description: "Find materials by course code, teacher name, keywords, or chapter titles.",
          icon: "Search",
          bgColor: "bg-blue-100",
          iconColor: "text-blue-600"
        },
        {
          id: 3,
          title: "One-Click Download",
          description: "Download materials instantly. No registration required for students.",
          icon: "Download",
          bgColor: "bg-red-100",
          iconColor: "text-red-600"
        },
        {
          id: 4,
          title: "Teacher Verified",
          description: "All materials are uploaded and verified by authorized university teachers.",
          icon: "BadgeCheck",
          bgColor: "bg-purple-100",
          iconColor: "text-purple-600"
        }
      ]);

    } catch (err) {
      console.error('Error fetching home page data:', err);
      setError('Failed to load platform data. Please try again.');

      // Set fallback data
      setPlatformStats([
        { id: 1, value: '500+', label: 'Materials', icon: 'book' },
        { id: 2, value: '1000+', label: 'Students', icon: 'users' },
        { id: 3, value: '50+', label: 'Teachers', icon: 'users' },
        { id: 4, value: '10+', label: 'Schools', icon: 'building' }
      ]);

      setPlatformFeatures([
        {
          id: 1,
          title: "Organized Repository",
          description: "All materials categorized by school, department, year, and course.",
          icon: "BookOpen",
          bgColor: "bg-green-100",
          iconColor: "text-green-600"
        },
        {
          id: 2,
          title: "Advanced Search",
          description: "Find materials by course code, teacher name, or keywords.",
          icon: "Search",
          bgColor: "bg-blue-100",
          iconColor: "text-blue-600"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialClick = (material) => {
    navigate(`/material/${material._id || material.id}`);
  };

  const handleSchoolClick = (school) => {
    navigate(`/school/${school._id || school.id}`);
  };

  const handleSearch = (query) => {
    if (!query.trim()) return;

    // Navigate to search page with query
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const handleQuickFilter = (filter) => {
    handleSearch(filter);
  };

  const handleStatClick = (stat) => {
    // Navigate based on which stat was clicked
    const label = stat.label.toLowerCase();
    if (label.includes('school')) {
      navigate('/browse');
    } else if (label.includes('material')) {
      navigate('/search');
    } else if (label.includes('teacher')) {
      navigate('/search?type=teacher');
    } else {
      navigate('/search');
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setShowQuickResults(false);
    navigate('/', { replace: true });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50">
      <Header />

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl mx-4 md:mx-8 mt-4 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-red-800">Unable to Load Data</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
            <button
              onClick={fetchAllData}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Retry
            </button>
          </div>
        </motion.div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-grid-white/10 bg-[size:20px_20px]" />
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-10 left-10 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
            rotate: [360, 180, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-10 right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl"
        />

        <div className="container relative mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 100 }}
              whileHover={{ scale: 1.05, rotate: 5 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-lg text-white px-6 py-3 rounded-full mb-6 cursor-pointer hover:from-blue-500/30 hover:to-purple-500/30 transition-all border border-white/20"

            >
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">Dire Dawa University</span>
              <ChevronRight className="w-4 h-4" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-200 to-green-200">
                Digital Learning
              </span>
              <br />
              <span className="relative inline-block">
                <span className="text-white">Repository</span>
                <motion.span
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 1 }}
                  className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-blue-400 to-green-400 rounded-full"
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="text-xl text-blue-100/90 mb-10 max-w-3xl mx-auto leading-relaxed"
            >
              Access thousands of verified course materials, lecture notes, and resources
              from all schools and departments. Free for students, powerful for teachers.
            </motion.p>

            {/* Interactive Stats */}
            <InteractiveStats stats={platformStats} onStatClick={handleStatClick} />

            {/* Enhanced Search Bar - Simplified like header search */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative max-w-2xl mx-auto"
            >
              <form onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim()) {
                  navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                }
              }}>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for courses, materials, or departments..."
                    className="w-full px-6 py-4 pr-32 rounded-full text-gray-900 bg-white shadow-2xl focus:outline-none focus:ring-4 focus:ring-blue-300 border-2 border-transparent focus:border-blue-400 transition-all text-lg"
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-full hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                  >
                    <Search className="w-5 h-5" />
                    <span>Search</span>
                  </button>
                </div>
              </form>

              <motion.div
                className="flex flex-wrap justify-center gap-3 mt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <span className="text-blue-200/90 text-sm flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Try searching for:
                </span>
                {[
                  { label: 'Data Structures', icon: Laptop },
                  { label: 'Database Systems', icon: FlaskConical },
                  { label: 'Software Engineering', icon: Cog },
                  { label: 'Business Analytics', icon: TrendingUp }
                ].map((filter) => (
                  <motion.button
                    key={filter.label}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setSearchQuery(filter.label);
                      navigate(`/search?q=${encodeURIComponent(filter.label)}`);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-full hover:bg-white/20 transition-all text-sm border border-white/20"
                  >
                    <filter.icon className="w-4 h-4" />
                    {filter.label}
                  </motion.button>
                ))}
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Animated waves */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-20" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="white" />
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="white" />
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="white" />
          </svg>
        </div>
      </section>
      {/* Recent Materials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <MaterialCarousel
            materials={recentMaterials}
            title="Fresh Content"
            subtitle="Recently Uploaded"
            onMaterialClick={handleMaterialClick}
            type="recent"
            loading={loading}
          />

          <div className="text-center mt-12">
            <Button
              variant="outline"
              size="large"
              icon={ArrowRight}
              onClick={() => navigate('/search?sort=recent')}
              className="group"
            >
              <span>View All Recent Materials</span>
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                →
              </motion.span>
            </Button>
          </div>
        </div>
      </section>

      {/* Most Downloaded Materials */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <MaterialCarousel
            materials={popularMaterials}
            title="Trending Now"
            subtitle="Most Downloaded"
            onMaterialClick={handleMaterialClick}
            type="popular"
            loading={loading}
          />

          <div className="text-center mt-12">
            <Button
              variant="outline"
              size="large"
              icon={Download}
              onClick={() => navigate('/search?sort=downloads')}
              className="group"
            >
              <span>View Top Downloads</span>
              <motion.span
                animate={{ y: [0, -2, 0] }}
                transition={{ duration: 0.5, repeat: Infinity }}
              >
                ↓
              </motion.span>
            </Button>
          </div>
        </div>
      </section>

      {/* School Explorer */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <SchoolExplorer
            schools={schools}
            onSchoolClick={handleSchoolClick}
            loading={loading}
          />


        </div>
      </section>

      {/* Platform Features */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-green-600">Our Platform</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Everything you need for successful learning and teaching
            </p>
          </motion.div>

          {platformFeatures.length > 0 ? (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {platformFeatures.map((feature, index) => (
                <motion.div
                  key={feature.id || index}
                  variants={itemVariants}
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.2 }
                  }}
                  className="group"
                >
                  <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 h-full overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-green-500/0 group-hover:from-blue-500/5 group-hover:to-green-500/5 transition-all duration-500" />

                    <div className={`absolute top-5 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${feature.bgColor || 'bg-gradient-to-r from-green-100 to-blue-100'
                      } w-16 h-16 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg`}>
                      <div className={`${feature.iconColor || 'text-green-600'} text-2xl`}>
                        {feature.icon === 'BookOpen' && '📚'}
                        {feature.icon === 'Search' && '🔍'}
                        {feature.icon === 'Download' && '📥'}
                        {feature.icon === 'BadgeCheck' && '✅'}
                        {!feature.icon && '🌟'}
                      </div>
                    </div>
                    <div className="pt-8 relative z-10">
                      <h3 className="font-bold text-lg mb-3 text-center">{feature.title}</h3>
                      <p className="text-gray-600 text-center mb-4">{feature.description}</p>
                      <div className="flex justify-center">
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm group-hover:bg-blue-50 group-hover:text-blue-700 transition-colors">
                          Learn more
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-12">
              <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No features information available</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800" />
        <div className="absolute inset-0 bg-grid-white/10 bg-[size:30px_30px]" />

        {/* Floating elements */}
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0],
            rotate: [0, 360, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/5 rounded-full blur-3xl"
        />

        <div className="container relative mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 text-white border border-white/20 shadow-2xl shadow-blue-900/30"
          >
            <div className="text-center max-w-3xl mx-auto">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="inline-flex p-4 bg-gradient-to-r from-blue-500/30 to-purple-500/30 backdrop-blur-sm rounded-full mb-6 border border-white/20"
              >
                <GraduationCap className="w-12 h-12" />
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold mb-4"
              >
                Join <span className="text-yellow-300">
                  {platformStats.find(s => s.label === 'Teachers')?.value || '100'}+
                </span> Teachers & Educators
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.25 }}
                className="text-blue-100/90 mb-8 max-w-2xl mx-auto text-lg"
              >
                Start exploring thousands of verified resources today. Free for students, powerful for educators.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.25 }}
                className="flex flex-wrap justify-center gap-4 mb-8"
              >
                <Button
                  variant="secondary"
                  size="large"
                  className="bg-gradient-to-r from- to-gray-100 text-blue-700 hover:from-gray-100 hover:to-white-100 shadow-lg hover:shadow-xl transition-all group"
                  onClick={() => navigate('/search')}
                >
                  <Sparkles className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                  Start Learning Free
                </Button>
                <Button
                  variant="outline"
                  size="large"
                  className="border-white text-white hover:bg-white/10 backdrop-blur-sm transition-all group"
                  onClick={() => navigate('/?register=true')}
                >
                  <Users className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Teacher Registration
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.25 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8"
              >
                {[
                  { icon: Clock, label: 'Updated Daily', value: '24/7', color: 'text-blue-300' },
                  { icon: Users, label: 'Active Teachers', value: platformStats.find(s => s.label === 'Teachers')?.value || '0', color: 'text-green-300' },
                  { icon: Download, label: 'Total Downloads', value: platformStats.find(s => s.label === 'Downloads')?.value || '0', color: 'text-purple-300' },
                  { icon: FileText, label: 'Verified Materials', value: platformStats.find(s => s.label === 'Materials')?.value || '0', color: 'text-yellow-300' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.25 }}
                    className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 transition-all cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                  >
                    <item.icon className={`w-6 h-6 ${item.color} mx-auto mb-2`} />
                    <div className="text-xl font-bold">{item.value}</div>
                    <div className="text-sm text-blue-200/80">{item.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;